import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { AnalysisComponentDetailList, CAFAverageTypes, METRIC_TAG } from '../../../../../model/caf.interface'
import { useCafAnalysisComponentDetailListV5 } from '../../../../../modules/apis/webview/caf'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../../modules/vars'
import {
  ComplexityAccordionProducts,
  ComplexityTabPanel,
  tabListMap as complexityTabListMap,
} from '../../analysis/complexity/ComplexityTabsSection'
import { Average, FluencyChart, tabListMap as fluencyTabListMap } from '../../analysis/fluency/FluencyTabsSection'
import {
  PronunciationAccordionProducts,
  Average as PronunciationAverage,
  PronunciationChart,
  pronunciationTabListMap,
} from '../../analysis/pronunciation/PronunciationTabsSection'
import { useCafSidePopup } from '../../common/CafSidePopup'
import HighlightedTitle from '../../common/HighlightedTitle'
import AccordionContainer from '../../highlight/AccordionContainer'
import SidePopupBody from './components/Body'

type AnalysisType = 'Complexity' | 'Accuracy' | 'Fluency' | 'Pronunciation' | null

const DEFAULT_PROPS = {
  hFull: true,
  bgWhite: true,
  maxW: 512,
  noTopSide: true,
  overlayClose: true,
}
const useMetricSidePopup = (analysisType: AnalysisType) => {
  const router = useRouter()
  const sidePopup = useCafSidePopup()
  const { t } = useTranslation()
  const { lessonId, tab, componentId, metricTag, metricId, errorType, back, errorTag } = router.query
  const { data, isLoading, refetch } = useCafAnalysisComponentDetailListV5(+componentId)
  const currentMetricData = data?.metrics?.find((metric) => metric.metric_tag === +metricTag)

  const descriptions = [t(n_caf.complexity.vocab_detail), t(n_caf.complexity.vocab_detail), '']
  const popupId = `${analysisType}_${metricTag}`
  useEffect(() => {
    if (
      (Number(lessonId) >= 0 && Number(metricTag) >= 0 && Number(componentId) >= 0 && !!data && !errorTag) ||
      back === 'true'
    ) {
      const title =
        analysisType === 'Complexity'
          ? complexityTabListMap[currentMetricData.metric_tag]?.title || ''
          : analysisType === 'Fluency'
          ? fluencyTabListMap[currentMetricData.metric_tag]?.title
          : analysisType === 'Pronunciation'
          ? pronunciationTabListMap[currentMetricData.metric_tag]?.title
          : ''

      sidePopup.show({
        ...DEFAULT_PROPS,
        id: popupId,
        body: (
          <SidePopupBody
            headerProps={{
              title: <HighlightedTitle size={20} title={t(title)} color={COLORS.PURPLE50} />,
              onClose: () => sidePopup.close(popupId),
            }}
          >
            <div className="flex w-[100%] flex-col items-start justify-start pr-[68px]">
              {getContent(data, analysisType, +metricTag, descriptions[+metricTag])}
            </div>
          </SidePopupBody>
        ),
        bgColor: COLORS.GRAY50,
      })
    }
  }, [metricTag, componentId, data, errorTag, back])
}

const getContent = (
  data: AnalysisComponentDetailList,
  analysisType: AnalysisType,
  metricTag: number,
  description: string
) => {
  if (!data?.metrics) return null
  const contentData = data.metrics.find((metric) => metric.metric_tag === metricTag)
  const fluencyUnit = fluencyTabListMap[metricTag]?.subtitle
  const type = METRIC_TAG[metricTag] as CAFAverageTypes
  return (
    <div>
      {analysisType == 'Complexity' && (
        <>
          <ComplexityTabPanel activeIndex={metricTag} graphData={contentData.graph_data?.data} />
          <div className="mt-[4px] mb-[8px] text-[14px] text-gray-500">{description}</div>
          <AccordionContainer>
            <ComplexityAccordionProducts data={data} activeIndex={metricTag} />
          </AccordionContainer>
        </>
      )}

      {analysisType === 'Fluency' && (
        <div className="max-w-[424px]">
          <FluencyChart graphData={contentData?.graph_data?.data} activeIndex={metricTag} />
          <Average thisClassAverage={contentData?.value || 0} withUnit={fluencyUnit} type={type} />
        </div>
      )}
      {analysisType === 'Pronunciation' && (
        <div className="max-w-[424px]">
          <PronunciationChart graphData={contentData?.graph_data?.data} activeIndex={metricTag} />
          <PronunciationAverage thisClassAverage={contentData?.value || 0} type={type} />
          <AccordionContainer>
            <PronunciationAccordionProducts data={data} activeIndex={metricTag} />
          </AccordionContainer>
        </div>
      )}
    </div>
  )
}

export default useMetricSidePopup
